
import { defineComponent, type PropType } from "vue"
import type { WeeklyReportDetailPayload } from "packs/models/WeeklyReport"
import ArticleGateReadinessNotice from "packs/pages/link/marketing/ArticleGateReadinessNotice.vue"

export default defineComponent({
    name: "WeeklyReportArticlePlans",
    components: { ArticleGateReadinessNotice },
    props: {
        detail: { type: Object as PropType<WeeklyReportDetailPayload>, required: true },
        creatingPlanIndex: { type: Number as PropType<number | null>, default: null },
    },
    emits: ["close", "create-draft"],
    computed: {
        decisionBlocked() {
            return this.detail.report.decision_status === "blocked"
        },
    },
    methods: {
        providerLabel(provider: string) {
            if (provider === "ga4") return "GA4"
            if (provider === "gsc") return "Search Console"
            if (provider === "wordpress") return "WordPress"
            return provider
        },
        priorityLabel(priority: string) {
            return priority === "high" ? "優先度 高" : priority === "medium" ? "優先度 中" : "優先度 低"
        },
        recommendationLabel(value: string) {
            return value === "rewrite_existing_article" ? "既存記事リライト" : "新規記事"
        },
        outcomeStatusLabel(value?: string) {
            return ({ ready: "準備完了", verified: "確認済み", indexed: "インデックス済み", pending: "反映待ち", warning: "要確認", blocked: "ブロック", manual_required: "手動確認" } as Record<string, string>)[value || ""] || "未確認"
        },
        outcomeActionLabel(value?: string) {
            return ({
                continue: "継続",
                scale: "展開を拡大",
                iterate_title_intro: "タイトル／導入を再検証",
                expand_answer: "回答範囲を拡張",
                add_first_party_evidence: "一次情報を追加",
                improve_internal_link: "内部リンクを改善",
                improve_cta: "CTAを改善",
                resolve_technical_issue: "技術問題を解消",
                rollback: "rollback",
                retire: "終了",
                insufficient_data: "データ不足",
                continue_monitoring: "継続観測",
                improve_title_intro: "タイトル／導入改善",
                expand_answer_scope: "回答範囲追加",
                add_evidence: "根拠追加",
                improve_author_publisher: "著者・publisher改善",
                add_internal_links: "内部リンク追加",
                resolve_technical_blocker: "技術ブロッカー解消",
                update_existing_candidate: "既存記事更新候補",
                no_new_article_candidate: "新規記事候補なし",
            } as Record<string, string>)[value] || value
        },
        aiAnswerActionLabel(value: string) {
            if (value === "new_article") return "新規記事"
            if (value === "update_existing") return "既存記事改善"
            return "見送り"
        },
        intentLabel(value: string) {
            return {
                problem_solving: "問題解決",
                comparison: "比較・選定",
                how_to: "方法・手順",
                definition: "定義・仕組み理解",
                constraints: "導入条件・制約確認",
                other: "その他",
            }[value] || value
        },
        contentGapLabel(value: string) {
            return value === "missing" ? "回答ページなし" : value === "partial" ? "回答不足" : "既存ページで充足"
        },
        scorePriority(score: number) {
            return score >= 70 ? "high" : score >= 40 ? "medium" : "low"
        },
        scoreFactorLabel(value: string) {
            return {
                goal_alignment: "目標との一致",
                gsc_demand: "GSC需要",
                ranking_opportunity: "順位改善余地",
                intent_clarity: "質問・判断意図",
                evidence_sufficiency: "根拠充足度",
                first_party_value: "自社独自情報",
                differentiation: "既存ページとの差別化",
                technical_blockers: "技術ブロッカー",
                cannibalization: "カニバリゼーション",
            }[value] || value
        },
        kpiText(values: Array<{ source: string; scope: string; metrics: string[] }>) {
            return values.map(value => `${value.source.toUpperCase()} ${value.scope}: ${value.metrics.join("、")}`).join("／")
        },
        goalStatusLabel(status: string) {
            if (status === "achieved") return "達成"
            if (status === "on_track") return "順調"
            if (status === "behind") return "未達"
            return "評価不能"
        },
        evidenceLabel(referenceId: string) {
            const evidence = this.detail.goal_analysis?.evidence?.find(item => item.id === referenceId)
            return evidence ? `${referenceId} — ${evidence.label}: ${evidence.display_value}` : referenceId
        },
        directionText(key: "continue_actions" | "change_actions" | "pause_actions" | "new_tests" | "kpis") {
            const values = this.detail.goal_analysis?.result?.next_week_direction?.[key] || []
            return values.length ? values.join("／") : "該当なし"
        },
        number(value: any) {
            return Number(value || 0).toLocaleString("ja-JP")
        },
        decimal(value: any) {
            return Number(value || 0).toFixed(1)
        },
    },
})
